<script setup lang="ts">
/**
 * The in-app sign-in states shared by ops, Network and the client portal
 * (knowledge/single-login/specs/2026-10-23-sign-in-surfaces-redesign/b-app-auth-states.md,
 * surfaces 1-4): signed out, sign-in could not finish, could not connect, no access, and
 * the short bridge shown while the tab leaves for the sign-in server.
 *
 * It wears the same shell as the sign-in server's pages (infrastructure/identity/themes/
 * claimshield/login/resources/css/claimshield.css) so moving between the app and sign-in
 * never changes the furniture: the Breathing Surface panel at >=1024px, a 440px column
 * centred in the viewport (Ahmed, 2026-10-23: centred, not top-aligned), the help line and
 * the footer centred under the action.
 *
 * Everything app-specific is passed in, never read off the hostname. `action` runs the
 * primary button; it may navigate away (the button keeps its busy label until the page
 * goes), or resolve `false` / throw to say it did not work, which restores the button and
 * announces the failure above it.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import logoHorizontal from '../../assets/logos/claimshield-horizontal.png'
import logoHorizontalReverse from '../../assets/logos/claimshield-horizontal-reverse.png'

import type { AppAuthFooterLink, AppAuthStateKind } from './app-auth-state'

const props = withDefaults(defineProps<{
  state: AppAuthStateKind
  /** Shown as the overline: "Operations", "Network", "Client portal". */
  appLabel: string
  /** Used in the no-access sentence: "ClaimShield Operations", "the ClaimShield client portal". */
  appName: string
  action: () => unknown
  footerLinks?: AppAuthFooterLink[]
  /** Replaces the no-access sentence and button, e.g. a staff account at the client portal
   * is sent to Operations rather than "Use a different account" (2026-11-04). */
  deniedBody?: string
  deniedAction?: string
}>(), {
  footerLinks: () => [
    { label: 'Terms of Service', href: 'https://claimshieldpro.com/terms' },
    { label: 'Privacy Policy', href: 'https://claimshieldpro.com/privacy' },
  ],
})

const HOME = 'https://claimshieldpro.com'
const BRIDGE_TEXT_MS = 300
const BRIDGE_STALL_MS = 10_000

interface Copy { title: string; body: string; action: string; busy: string; home: boolean }
const COPY: Record<Exclude<AppAuthStateKind, 'bridge'>, Copy> = {
  'signed-out': {
    title: "You're signed out", body: 'Sign in again whenever you are ready.',
    action: 'Sign in again', busy: 'Opening sign-in…', home: false,
  },
  failed: {
    title: "We couldn't finish signing you in", body: "Try again. If it still doesn't work, call us for help.",
    action: 'Try again', busy: 'Trying again…', home: true,
  },
  unreachable: {
    title: "We couldn't connect to ClaimShield", body: 'Check your internet connection, then try again.',
    action: 'Try again', busy: 'Trying again…', home: false,
  },
  denied: {
    title: "This account doesn't have access", body: '',
    action: 'Use a different account', busy: 'Opening sign-in…', home: true,
  },
}

/** The bridge turns into "couldn't finish" if the tab is still here after 10 seconds. */
const stalled = ref(false)
const shown = computed<AppAuthStateKind>(() => (props.state === 'bridge' && stalled.value ? 'failed' : props.state))
const copy = computed(() => {
  if (shown.value === 'bridge') return null
  const c = COPY[shown.value]
  if (shown.value !== 'denied') return c
  return {
    ...c,
    body: props.deniedBody ?? `To open ${props.appName}, use a different account or call us for help.`,
    action: props.deniedAction ?? c.action,
    busy: props.deniedAction ? 'Opening…' : c.busy,
  }
})

const busy = ref(false)
const failedOnce = ref(false)
const bridgeText = ref(false)
const heading = ref<HTMLElement | null>(null)
const year = new Date().getFullYear()

let textTimer: ReturnType<typeof setTimeout> | undefined
let stallTimer: ReturnType<typeof setTimeout> | undefined
function clearTimers() { clearTimeout(textTimer); clearTimeout(stallTimer) }
function armBridge() {
  clearTimers()
  bridgeText.value = false
  stalled.value = false
  if (props.state !== 'bridge') return
  textTimer = setTimeout(() => { bridgeText.value = true }, BRIDGE_TEXT_MS)
  stallTimer = setTimeout(() => { stalled.value = true }, BRIDGE_STALL_MS)
}

async function run() {
  if (busy.value) return
  busy.value = true
  failedOnce.value = false
  let ok = true
  try {
    ok = (await props.action()) !== false
  } catch {
    ok = false
  }
  if (!ok) {
    busy.value = false
    failedOnce.value = true
  }
}

/** A state change inside the app moves focus to the new heading, so it is read out. A fresh
 * page load does not: the reader starts at the top anyway, and a ring on arrival is noise. */
async function focusHeading() {
  await nextTick()
  heading.value?.focus({ preventScroll: true })
}

watch(() => props.state, () => { armBridge(); busy.value = false; failedOnce.value = false })
watch(shown, (s) => { if (s !== 'bridge') void focusHeading() })
onMounted(armBridge)
onBeforeUnmount(clearTimers)
</script>

<template>
  <!-- The 100-600ms handoff: identity at once, words only if it takes longer than 300ms. -->
  <div v-if="shown === 'bridge'" class="cs-auth cs-auth-bridge">
    <div class="cs-auth-col">
      <img class="cs-auth-logo" :src="logoHorizontal" alt="ClaimShield">
      <p class="cs-auth-label">{{ appLabel }}</p>
      <p class="cs-auth-bridge-text" role="status" aria-live="polite">{{ bridgeText ? 'Opening sign-in…' : '' }}</p>
    </div>
  </div>

  <div v-else class="cs-auth">
    <aside class="cs-auth-panel" aria-hidden="true">
      <div class="cs-auth-mesh" />
      <img class="cs-auth-mark" :src="logoHorizontalReverse" alt="">
      <p class="cs-auth-tagline">Your fleet. Your data.<br>Always current.</p>
    </aside>

    <main class="cs-auth-side">
      <div class="cs-auth-col">
        <img class="cs-auth-logo cs-auth-logo-sm" :src="logoHorizontal" alt="ClaimShield">
        <p class="cs-auth-label">{{ appLabel }}</p>
        <h1 ref="heading" class="cs-auth-title" tabindex="-1">{{ copy?.title }}</h1>
        <p class="cs-auth-body">{{ copy?.body }}</p>

        <p v-if="failedOnce" class="cs-auth-retry-note" role="alert">That didn't work. Please try again or call us.</p>
        <button type="button" class="cs-auth-btn" :disabled="busy" :aria-busy="busy" @click="run">
          {{ busy ? copy?.busy : copy?.action }}
        </button>

        <p class="cs-auth-help">Need help? Call ClaimShield <a href="tel:+13173609505">(317) 360-9505</a>.</p>
        <p v-if="copy?.home" class="cs-auth-home"><a :href="HOME">Return to ClaimShield</a></p>

        <footer class="cs-auth-foot">
          <p>&copy; {{ year }} ClaimShield<span class="cs-auth-dot">&middot;</span>Safety &amp; Compliance Services</p>
          <p>
            <template v-for="(link, i) in footerLinks" :key="link.href">
              <span v-if="i" class="cs-auth-dot">&middot;</span>
              <a :href="link.href" :target="link.href.startsWith('http') ? '_blank' : undefined"
                 :rel="link.href.startsWith('http') ? 'noopener noreferrer' : undefined">{{ link.label }}</a>
            </template>
          </p>
        </footer>
      </div>
    </main>
  </div>
</template>

<style scoped>
/* Values mirror the sign-in theme (claimshield.css) and knowledge/brand/tokens.md:
   purple #3d2683 / dark #2a1a5e / light #6b4faf, purple-tinted warm grey text,
   radii 24 (panel) and 8 (control), focus glow rgba(107,79,175,.16). */
.cs-auth {
  --ink: #2a1a5e; --text: #3d3a4f; --purple: #3d2683; --purple-dark: #2a1a5e; --purple-light: #6b4faf;
  display: flex; min-height: 100vh; min-height: 100dvh; background: #fff; color: var(--ink);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}
.cs-auth *, .cs-auth *::before, .cs-auth *::after { box-sizing: border-box; }

/* Breathing Surface (playbook 3.2): frozen recipe, 6s orbit, reduced-motion guard. */
.cs-auth-panel {
  position: relative; overflow: hidden; display: none; flex-direction: column; justify-content: space-between;
  width: calc(58.333% - 24px); margin: 12px; padding: 40px; border-radius: 24px;
}
.cs-auth-mesh {
  position: absolute; inset: 0;
  background:
    radial-gradient(ellipse at 20% 50%, rgba(107, 79, 175, .9) 0%, transparent 60%),
    radial-gradient(ellipse at 70% 20%, rgba(139, 92, 246, .7) 0%, transparent 50%),
    radial-gradient(ellipse at 40% 80%, rgba(59, 130, 246, .5) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 70%, rgba(168, 85, 247, .6) 0%, transparent 45%),
    radial-gradient(ellipse at 10% 20%, rgba(236, 72, 153, .3) 0%, transparent 50%),
    linear-gradient(135deg, var(--purple-dark), var(--purple-light));
  background-size: 200% 200%;
  animation: cs-auth-breathe 6s ease-in-out infinite;
}
.cs-auth-mesh::after { content: ''; position: absolute; inset: 0; backdrop-filter: blur(40px); -webkit-backdrop-filter: blur(40px); }
@keyframes cs-auth-breathe {
  0%, 100% { background-position: 0% 50%; }
  25% { background-position: 100% 25%; }
  50% { background-position: 50% 100%; }
  75% { background-position: 25% 0%; }
}
.cs-auth-panel > *:not(.cs-auth-mesh) { position: relative; z-index: 1; }
.cs-auth-mark { height: 36px; width: auto; align-self: flex-start; }
.cs-auth-tagline { margin: 0; font-size: 24px; line-height: 1.25; font-weight: 600; color: #fff; }

/* Content column: centred in the viewport; a taller page grows and starts at the top. */
.cs-auth-side { flex: 1; display: flex; flex-direction: column; justify-content: center; padding: 32px 24px 40px; }
.cs-auth-col { width: 100%; max-width: 440px; margin: 0 auto; }
.cs-auth-logo { display: block; height: 36px; width: auto; margin-bottom: 32px; }

.cs-auth-label {
  margin: 0 0 8px; font-size: 12px; line-height: 1.4; font-weight: 600; letter-spacing: .08em;
  text-transform: uppercase; color: var(--purple-light);
}
.cs-auth-title { margin: 0; font-size: 24px; line-height: 1.25; font-weight: 600; letter-spacing: -0.015em; color: var(--ink); }
.cs-auth-title:focus, .cs-auth-title:focus-visible { outline: none !important; box-shadow: none !important; }
.cs-auth-body { margin: 8px 0 0; font-size: 16px; line-height: 1.55; color: var(--text); }
.cs-auth-retry-note { margin: 20px 0 0; font-size: 15px; line-height: 1.45; color: var(--text); }

.cs-auth-btn {
  display: flex; align-items: center; justify-content: center; width: 100%; min-height: 48px; margin-top: 24px;
  padding: 11px 22px; border: 0; border-radius: 8px; cursor: pointer;
  font: inherit; font-size: 16px; font-weight: 500; color: #fff; background: var(--purple);
  transition: background-color .15s;
}
.cs-auth-retry-note + .cs-auth-btn { margin-top: 12px; }
.cs-auth-btn:hover, .cs-auth-btn:active { background: var(--purple-dark); }
.cs-auth-btn:active { transform: scale(.97); transition: transform .1s ease-out; }
.cs-auth-btn:disabled { cursor: default; opacity: .8; }
.cs-auth-btn:focus-visible { outline: 2px solid var(--purple-light); outline-offset: 2px; box-shadow: 0 0 0 3px rgba(107, 79, 175, .16); }

.cs-auth-help { margin: 32px 0 0; text-align: center; font-size: 15px; line-height: 1.55; color: var(--text); }
.cs-auth-help a, .cs-auth-home a { color: var(--purple); font-weight: 500; text-decoration: underline; text-underline-offset: 2px; white-space: nowrap; }
.cs-auth-home { margin: 8px 0 0; text-align: center; font-size: 15px; line-height: 1.55; }
.cs-auth-home a { display: inline-flex; align-items: center; min-height: 44px; }
.cs-auth-help a:focus-visible, .cs-auth-home a:focus-visible, .cs-auth-foot a:focus-visible {
  outline: 2px solid var(--purple-light); outline-offset: 2px; border-radius: 4px;
}

.cs-auth-foot { margin-top: 24px; text-align: center; font-size: 13px; line-height: 1.6; color: var(--text); }
.cs-auth-foot p { margin: 0; }
.cs-auth-foot p + p { margin-top: 6px; }
.cs-auth-foot a { color: inherit; text-decoration: underline; text-underline-offset: 2px; }
.cs-auth-foot a:hover { color: var(--purple); }
.cs-auth-dot { margin: 0 6px; }

/* The bridge: white, quiet, no panel, no button, nothing that moves. */
.cs-auth-bridge { align-items: center; justify-content: center; padding: 24px; }
.cs-auth-bridge .cs-auth-col { text-align: center; }
.cs-auth-bridge .cs-auth-logo { margin: 0 auto 40px; }
.cs-auth-bridge-text { min-height: 1.55em; margin: 0; font-size: 16px; line-height: 1.55; color: var(--text); }

@media (min-width: 1024px) {
  .cs-auth-panel { display: flex; }
  .cs-auth-side { width: 41.667%; flex: none; padding: 64px 56px 48px; }
  .cs-auth-logo-sm { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .cs-auth-mesh { animation: none; }
  .cs-auth-btn:active { transform: none; }
}
@media (prefers-reduced-transparency: reduce) {
  .cs-auth-mesh::after { backdrop-filter: none; -webkit-backdrop-filter: none; }
}
@media (prefers-contrast: more) {
  .cs-auth-btn:focus-visible { outline-color: var(--ink); }
  .cs-auth-body, .cs-auth-help, .cs-auth-foot { color: var(--ink); }
}
</style>
